/**
 * Decision Loop Store
 *
 * Reads/updates cycle_decision_loops (seeded per cycle from registry_decision_classes).
 * No client-side seeding; no sequence or ordering. Loops support parallel execution.
 */

import { create } from 'zustand';
import { getSafeSupabaseClient } from '../../lib/supabaseSync';

export type CycleDecisionLoopStatus = 'NOT_STARTED' | 'IN_PROGRESS' | 'COMPLETE';

export interface CycleDecisionLoop {
  id: string;
  cycleId: string;
  decisionClassId: string;
  poolId: string | null;
  status: CycleDecisionLoopStatus;
  assignedParticipants: string[];
  completedAt: Date | null;
  createdAt: Date;
  updatedAt: Date;
}

interface DecisionLoopStoreState {
  getLoopsForCycle: (cycleId: string) => Promise<CycleDecisionLoop[]>;
  markInProgress: (loopId: string) => Promise<void>;
  markComplete: (loopId: string) => Promise<void>;
  assignParticipants: (loopId: string, participantIds: string[]) => Promise<void>;
}

function fromRow(row: Record<string, unknown>): CycleDecisionLoop {
  return {
    id: row.id as string,
    cycleId: row.cycle_id as string,
    decisionClassId: row.decision_class_id as string,
    poolId: (row.pool_id as string) ?? null,
    status: (row.status as CycleDecisionLoopStatus) ?? 'NOT_STARTED',
    assignedParticipants: ((row.assigned_participants as string[]) ?? []).filter(Boolean),
    completedAt: row.completed_at ? new Date(row.completed_at as string) : null,
    createdAt: new Date(row.created_at as string),
    updatedAt: new Date(row.updated_at as string),
  };
}

export const useDecisionLoopStore = create<DecisionLoopStoreState>()(() => ({
  getLoopsForCycle: async (cycleId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return [];
    const { data, error } = await supabase
      .from('cycle_decision_loops')
      .select('*')
      .eq('cycle_id', cycleId);
    if (error) {
      console.error('[DecisionLoopStore] getLoopsForCycle error:', error);
      return [];
    }
    return (data ?? []).map(fromRow);
  },

  markInProgress: async (loopId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('cycle_decision_loops')
      .update({ status: 'IN_PROGRESS', updated_at: new Date().toISOString() })
      .eq('id', loopId);
  },

  markComplete: async (loopId: string) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    const now = new Date().toISOString();
    await supabase
      .from('cycle_decision_loops')
      .update({ status: 'COMPLETE', completed_at: now, updated_at: now })
      .eq('id', loopId);
  },

  assignParticipants: async (loopId: string, participantIds: string[]) => {
    const supabase = getSafeSupabaseClient();
    if (!supabase) return;
    await supabase
      .from('cycle_decision_loops')
      .update({
        assigned_participants: participantIds,
        updated_at: new Date().toISOString(),
      })
      .eq('id', loopId);
  },
}));
